<template>
  <div :data-view-id="uniqueTitle">
  <b-container fluid>
    <ViewTitle :title="title" />
    <b-row v-if="visiblePresetFilterButtons.length" class="pb-1">
      <b-col>
        <PresetFilterButtons
          :buttons="visiblePresetFilterButtons"
          :active-ids="activePresetFilterIds"
          @toggle="onPresetFilterToggle"
        />
      </b-col>
    </b-row>
    <b-row v-if="searchEnabled" class="pb-1">
      <b-col>
        <ViewSearch :model-value="searchText" @update:model-value="onSearchInput" />
      </b-col>
    </b-row>
    <b-row v-if="userFiltering" class="pb-1">
      <b-col>
        <RuntimeFilterBuilder
          ref="filterBuilder"
          :field-groups="filterFieldGroups"
          :initial-filters="resolvedInitialFilters"
          :initial-match-mode="resolvedInitialMatchMode"
          @filter-changed="onFilterChanged"
        />
      </b-col>
    </b-row>
    <b-row v-if="csvExport || pageChoices.length" class="table-export-row">
      <b-col v-if="pageChoices.length" class="table-page-size">
        <label>
          Rows
          <select class="table-page-size-select" :value="chosenPageSize" @change="changePageSize($event.target.value)">
            <option v-for="size in pageChoices" :key="size" :value="size">{{ size }}</option>
          </select>
        </label>
        <button type="button" class="table-export-btn" :disabled="pageOffset === 0" @click="changePage(-1)">Previous</button>
        <button type="button" class="table-export-btn" :disabled="!hasNextPage" @click="changePage(1)">Next</button>
      </b-col>
      <b-col v-if="csvExport" class="text-end">
        <button type="button" class="table-export-btn" :disabled="csvExporting" @click="exportTable('csv')">CSV</button>
        <button type="button" class="table-export-btn" :disabled="csvExporting" @click="exportTable('text')">Text</button>
        <button type="button" class="table-export-btn" :disabled="csvExporting" @click="exportTable('json')">JSON</button>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-table
          class="table-responsive"
          :class="{ 'table-wrap-headers': layout.wrapHeaders }"
          :responsive="!columnFitState.narrow"
          :stacked="columnFitState.narrow"
          striped
          hover
          :fields="tableFields"
          :items="displayItems"
          :sort-by="sortBy"
          no-local-sorting
          @update:sort-by="onSortByUpdate"
        >
          <template #head()="data">
            <span class="table-head-label">{{ data.label }}</span>
            <span class="table-col-resize" title="Drag to resize" @mousedown.prevent="startResize(data.field, $event)" @click.stop />
          </template>
          <template #cell()="data">
            <template v-if="data.field.type === 'action'">
              <button
                v-if="actionVisible(data.item, data.field)"
                type="button"
                class="table-export-btn"
                :disabled="actionState[data.item._rowId + '_' + data.field.columnUniqueTitle] === 'running'"
                @click="runAction(data.item, data.field)"
              >
                {{ data.field.label }}
              </button>
              <small v-if="actionState[data.item._rowId + '_' + data.field.columnUniqueTitle] === 'error'" class="editable-cell-error">
                {{ actionErrors[data.item._rowId + '_' + data.field.columnUniqueTitle] }}
              </small>
            </template>
            <template v-else-if="editableFieldMap[data.field.key]">
              <div class="editable-cell">
                <label class="visually-hidden" :for="cellInputId(data)">{{ data.field.label }}</label>
                <input
                  :id="cellInputId(data)"
                  class="editable-cell-input"
                  :class="{ 'editable-cell-saving': cellSaveState[data.item._rowId + '_' + data.field.key] === 'saving', 'editable-cell-saved': cellSaveState[data.item._rowId + '_' + data.field.key] === 'saved' }"
                  :value="getRawValue(data.item, data.field.key)"
                  :placeholder="editableFieldMap[data.field.key].placeholder"
                  :required="editableFieldMap[data.field.key].required || undefined"
                  :aria-describedby="editableFieldMap[data.field.key].description ? cellInputId(data) + '-description' : undefined"
                  :aria-invalid="cellErrors[data.item._rowId + '_' + data.field.key] ? 'true' : undefined"
                  @change="onCellEdit(data.item, data.field.key, $event)"
                />
                <small v-if="editableFieldMap[data.field.key].description" :id="cellInputId(data) + '-description'" class="editable-cell-desc">
                  {{ editableFieldMap[data.field.key].description }}
                </small>
                <small v-if="cellErrors[data.item._rowId + '_' + data.field.key]" class="editable-cell-error">
                  {{ cellErrors[data.item._rowId + '_' + data.field.key] }}
                </small>
              </div>
            </template>
            <template v-else-if="linkFieldMap[data.field.key]">
              <button
                v-if="linkFieldMap[data.field.key].linkMode === 'code'"
                type="button"
                class="table-link"
                :class="linkFieldMap[data.field.key].displayAsButton ? 'view-link-button' : 'table-code-link'"
                @click="runLinkCode(data.item, data.field, $event)"
              >
                {{ getLinkText(data.item, data.field) }}
              </button>
              <router-link
                v-else-if="getLinkUrl(data.item, data.field)"
                :to="getLinkUrl(data.item, data.field)"
                class="table-link"
                :class="{ 'view-link-button': linkFieldMap[data.field.key].displayAsButton }"
              >
                {{ getLinkText(data.item, data.field) }}
              </router-link>
              <span v-else :class="linkFieldMap[data.field.key].displayAsButton ? 'view-link-button is-disabled' : 'table-link-disabled'">
                {{ getLinkText(data.item, data.field) }}
              </span>
            </template>
            <span v-else-if="blankCell(data)" class="table-empty-text">{{ data.field.emptyText }}</span>
            <span v-else v-html="shownCell(data)" />
          </template>
        </b-table>
        <div v-if="summaryLine" class="table-summary" role="status">{{ summaryLine }}</div>
        <div class="visually-hidden" aria-live="polite">{{ displayItems.length }} records</div>
      </b-col>
    </b-row>
  </b-container>
  </div>
</template>

<script>
import _ from 'lodash'
import axios from 'axios'
import { reactive } from 'vue'
import RuntimeFilterBuilder from './RuntimeFilterBuilder.vue'
import ViewTitle from './ViewTitle.vue'
import ViewSearch from './ViewSearch.vue'
import { formatMultiselectHtml } from '../lib/multiselect-display'
import { fieldDisplayValue, shownField } from '../../../shared/field-display'
import { realtimeSubscribeFields } from '../lib/view-visibility'
import { liveAppWebSocketUrl } from '../lib/live-app-socket'
import { userSortsFromHeader } from '../../../shared/view-sort'
import { viewSearchParam } from '../../../shared/view-search'
import { eventRefreshesView, fieldIndexMap } from '../../../shared/view-refresh'
import { filterQueryHref, readFilterQuery } from '../lib/filter-query'
import PresetFilterButtons from './PresetFilterButtons.vue'
import { presetFilterMixin } from '../lib/preset-filter-state'
import { formattingToStyleString } from '../../../shared/display-rules'
import { formatAppInstant } from '../lib/app-time-zone'
import { imageCellHtml } from '../../../shared/image-column'
import { escapeHtml } from '../../../shared/html-sanitize'
import { recordFailures } from '../../../shared/column-validation.js'
import { runViewLinkCode } from '../lib/run-custom-code'
import {
  normalizeTableLayout,
  pageSizeChoices,
  clampPageSize,
  isEmptyCell,
  tableActionAllowed
} from '../../../shared/table-layout.js'

export default {
  mixins: [presetFilterMixin],
  inject: {
    columnFitState: { default: () => ({ narrow: false }) }
  },
  components: {
    RuntimeFilterBuilder,
    ViewTitle,
    ViewSearch,
    PresetFilterButtons
  },
  data () {
    return {
      filterFieldGroups: [],
      filteredItems: null,
      urlFilters: null,
      urlMatchMode: null,
      sortBy: undefined,
      activeFilterEvent: null,
      searchText: '',
      csvExporting: false,
      columnSummary: {},
      chosenPageSize: null,
      pageOffset: 0,
      hasNextPage: false,
      columnWidths: {},
      actionState: reactive({}),
      actionErrors: reactive({}),
      ws: null,
      cellSaveState: reactive({}),
      cellErrors: reactive({})
    }
  },
  props: {
    uniqueTitle: { type: String, default: '' },
    title: { type: String, default: '' },
    userFiltering: { type: Boolean },
    realtime: { type: Boolean, default: false },
    spreadSheetUniqueTitle: { type: String, default: '' },
    fields: { type: Array, default: () => [] },
    items: { type: Array, default: () => [] },
    pageUniqueTitle: { type: String, default: '' },
    componentUniqueTitle: { type: String, default: '' },
    fieldCounterMap: { type: Object, default: null },
    defaultUserFilters: { type: Array, default: () => [] },
    defaultUserFilterMatchMode: { type: String, default: 'all' },
    restrictUserFiltering: { type: Boolean, default: false },
    csvExport: { type: Boolean, default: false },
    searchEnabled: { type: Boolean, default: false },
    tableLayout: { type: Object, default: () => ({}) }
  },
  computed: {
    displayItems () {
      return this.filteredItems !== null ? this.filteredItems : this.items
    },
    layout () {
      return normalizeTableLayout(this.tableLayout)
    },
    pageChoices () {
      return pageSizeChoices(this.layout)
    },
    tableFields () {
      return (this.fields || []).filter((field) => !this.columnHidden(field)).map((field) => {
        const width = this.columnWidths[field.key] || field.width
        const tdAttr = {
          ...(field.tdAttr || {}),
          'data-label': field.label == null ? (field.columnUniqueTitle || '') : field.label
        }

        if (field.columnUniqueTitle) tdAttr['data-field'] = field.columnUniqueTitle

        const next = {
          ...field,
          tdAttr
        }

        if (width) {
          next.thStyle = { width: `${width}px`, minWidth: `${width}px` }
          next.tdStyle = { width: `${width}px` }
        }

        return next
      })
    },
    summaryLine () {
      const parts = []

      for (const field of this.fields || []) {
        if (!field.summary) continue
        const value = this.columnSummary[field.columnUniqueTitle]
        if (value == null) continue
        const shown = field.summary === 'average' ? Math.round(value * 100) / 100 : value
        parts.push(`${field.label} ${field.summary} ${shown}`)
      }

      return parts.join(' · ')
    },
    filterQueryKey () {
      return `f_${this.componentUniqueTitle || this.uniqueTitle}`
    },
    searchQueryKey () {
      return viewSearchParam(this.componentUniqueTitle || this.uniqueTitle)
    },
    searchTerm () {
      return String(this.searchText || '').trim()
    },
    resolvedInitialFilters () {
      if (this.urlFilters) return this.urlFilters
      return this.defaultUserFilters
    },
    resolvedInitialMatchMode () {
      if (this.urlMatchMode) return this.urlMatchMode
      return this.defaultUserFilterMatchMode
    },
    editableFieldMap () {
      const map = {}
      if (!this.fields) return map
      for (const field of this.fields) {
        if (field.editable) {
          map[field.key] = {
            placeholder: field.placeholder || '',
            description: field.description || '',
            columnUniqueTitle: field.columnUniqueTitle || '',
            required: !!field.required,
            label: field.label || field.columnUniqueTitle || '',
            columnType: field.columnType || '',
            validationRules: field.validationRules || { rules: [] }
          }
        }
      }
      return map
    },
    linkFieldMap () {
      const map = {}
      if (!this.fields) return map
      for (const field of this.fields) {
        if (field.type === 'link') {
          map[field.key] = {
            childPageUniqueTitle: field.childPageUniqueTitle || '',
            linkMode: field.linkMode || 'record',
            relationshipColumn: field.relationshipColumn || null,
            linkKey: field.linkKey || field.childPageUniqueTitle,
            customText: field.customText || '',
            code: field.code || '',
            displayAsButton: !!field.displayAsButton
          }
        }
      }
      return map
    }
  },
  mounted () {

    this.chosenPageSize = clampPageSize(null, this.tableLayout)
    this.noteFullPage(this.items)
    this.loadSummary()
    if (window.__superturbo_preview_mode) return
    if (this.userFiltering) {
      this.readFiltersFromUrl()
      this.loadFilterFields()
    }
    if (this.searchEnabled) {
      this.readSearchFromUrl()
      if (this.searchTerm) this.fetchTableData({ alwaysApply: true })
    }
    if (this.realtime) {
      this.connectRealtimeWebSocket()
    }
    this._onRefreshViews = (event) => {
      if (!eventRefreshesView(event, this.componentUniqueTitle || this.uniqueTitle)) return
      this.refreshData()
    }
    window.addEventListener('superturbo-refresh-views', this._onRefreshViews)
  },
  beforeUnmount () {
    clearTimeout(this._searchTimer)
    this.disconnectRealtimeWebSocket()
    if (this._onRefreshViews) {
      window.removeEventListener('superturbo-refresh-views', this._onRefreshViews)
    }
  },
  watch: {
    items (rows) {

      this.noteFullPage(rows)
      this.loadSummary()
    },
    tableLayout: {
      deep: true,
      handler () {

        this.chosenPageSize = clampPageSize(this.chosenPageSize, this.tableLayout)
      }
    },
    userFiltering (val) {
      if (val && !this.filterFieldGroups.length) {
        this.loadFilterFields()
      }
    }
  },
  methods: {
    cellInputId (data) {
      return `cell-${this.componentUniqueTitle || this.uniqueTitle}-${data.item._rowId}-${data.field.key}`
    },
    getLinkUrl (item, field) {
      const linkInfo = this.linkFieldMap[field.key]
      if (!linkInfo) return null
      const linkKey = linkInfo.linkKey
      const rawLink = (item._links && item._links[linkKey]) || (item.links && item.links[linkKey])
      if (!rawLink) return null
      if (rawLink.includes('/')) {
        return `${(this.routePath || this.$route.path).replace(/\/+$/, '')}/${rawLink}`
      }
      return `/${this.$route.params.application}/${rawLink}`
    },
    runLinkCode (item, field, event) {

      const info = this.linkFieldMap[field.key] || field
      const row = event && event.currentTarget && event.currentTarget.closest('tr')

      return runViewLinkCode(info.code, item, row, 'row')
    },
    getLinkText (item, field) {
      const linkInfo = this.linkFieldMap[field.key]
      // Use raw value if available (column display), otherwise custom text
      const rawVal = this.getRawValue(item, field.key)
      if (rawVal && rawVal !== '') return rawVal
      if (linkInfo && linkInfo.customText) return linkInfo.customText
      return 'View'
    },
    getRawValue (item, fieldKey) {
      if (item._raw && item._raw[fieldKey] != null) {
        return item._raw[fieldKey]
      }
      return item[fieldKey] || ''
    },
    async onCellEdit (item, fieldKey, event) {
      const newValue = event.target.value
      const rowId = item._rowId
      const fieldConfig = this.editableFieldMap[fieldKey]

      if (!rowId || !fieldConfig || !fieldConfig.columnUniqueTitle) {
        console.warn('Cannot save editable cell: missing rowId or field config')
        return
      }

      const cellKey = `${rowId}_${fieldKey}`
      const failures = recordFailures([{
        uniqueTitle: fieldConfig.columnUniqueTitle,
        title: fieldConfig.label,
        type: fieldConfig.columnType || 'text',
        required: fieldConfig.required,
        validationRules: fieldConfig.validationRules
      }], { [fieldConfig.columnUniqueTitle]: newValue })

      if (failures.length) {
        this.cellSaveState[cellKey] = 'error'
        this.cellErrors[cellKey] = failures[0].message
        event.target.value = this.getRawValue(item, fieldKey)
        return
      }

      this.cellErrors[cellKey] = ''
      this.cellSaveState[cellKey] = 'saving'

      try {
        const pageUT = this.pageUniqueTitle || this.routePageUniqueTitle || window.__superturbo_getPageUniqueTitle?.()
        const compUT = this.componentUniqueTitle || this.uniqueTitle
        const account = this.routeAccount
        const application = this.$route?.params?.application || ''

        const data = new FormData()
        data.append(fieldConfig.columnUniqueTitle, newValue)

        await axios.put(
          `${VUE_APP_PROXY_SERVER_URL}/${account}/${application}/pages/${pageUT}/components/${compUT}/accesskey/${rowId}`,
          data,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        )

        // Update the raw value in the item
        if (item._raw) {
          item._raw[fieldKey] = newValue
        }

        this.cellSaveState[cellKey] = 'saved'
        setTimeout(() => {
          this.cellSaveState[cellKey] = null
        }, 1500)
      } catch (err) {
        console.warn('Failed to save cell edit:', err)
        const data = err && err.response && err.response.data
        this.cellErrors[cellKey] = typeof data === 'string' && data.trim() ? data : 'Could not save this cell.'
        this.cellSaveState[cellKey] = 'error'
        event.target.value = this.getRawValue(item, fieldKey)
      }
    },
    async loadFilterFields () {
      const pageUT = this.pageUniqueTitle || this.routePageUniqueTitle || window.__superturbo_getPageUniqueTitle?.()
      const compUT = this.componentUniqueTitle || this.uniqueTitle
      if (!pageUT || !compUT) return

      const account = this.routeAccount
      const application = this.$route?.params?.application || ''

      try {
        let url = `${VUE_APP_PROXY_SERVER_URL}/${account}/${application}/pages/${pageUT}/components/${compUT}/filterfields`
        if (this.restrictUserFiltering) {
          url += '?restrict=true'
        }
        const response = await axios.get(url)
        this.filterFieldGroups = response.data
      } catch (err) {
        console.warn('Failed to load filter fields:', err)
      }
    },
    compactFilter (filter) {
      const compact = {}
      for (const [k, v] of Object.entries(filter)) {
        if (v === null || v === undefined) continue
        if (k === 'valueFieldRef' && v !== null) {
          compact[k] = this.compactFilter(v)
        } else {
          compact[k] = v
        }
      }
      return compact
    },
    expandFilter (compact) {
      const defaults = {
        relationshipColumn: null,
        relatedSpreadsheet: null,
        sourceSpreadsheet: null,
        sourceRelationshipColumn: null,
        relQuantifier: null,
        valueType: 'literal',
        value: '',
        valueFieldRef: null
      }
      const full = { ...defaults, ...compact }
      if (full.valueFieldRef && typeof full.valueFieldRef === 'object') {
        full.valueFieldRef = this.expandFilter(full.valueFieldRef)
      }
      return full
    },
    filtersMatchDefaults (filterEvent) {
      const defaults = this.defaultUserFilters || []
      const current = (filterEvent && filterEvent.filters) || []
      const currentMode = (filterEvent && filterEvent.matchMode) || 'all'
      const defaultMode = this.defaultUserFilterMatchMode || 'all'
      if (currentMode !== defaultMode) return false
      if (current.length !== defaults.length) return false
      return JSON.stringify(current) === JSON.stringify(defaults)
    },
    readFiltersFromUrl () {
      const parsed = readFilterQuery(window.location.search, this.filterQueryKey)
      if (!parsed) return
      this.urlFilters = parsed.filters.map(f => this.expandFilter(f))
      this.urlMatchMode = parsed.matchMode
    },
    writeFiltersToUrl (filterEvent) {
      try {
        const hasFilters = filterEvent && filterEvent.filters && filterEvent.filters.length > 0
        let payload = null
        if (hasFilters && !this.filtersMatchDefaults(filterEvent)) {
          payload = { filters: filterEvent.filters.map(f => this.compactFilter(f)) }
          if ((filterEvent.matchMode || 'all') !== 'all') {
            payload.matchMode = filterEvent.matchMode
          }
        }
        window.history.replaceState(null, '', filterQueryHref(window.location.href, this.filterQueryKey, payload))
      } catch (err) {
        console.warn('Failed to write filters to URL:', err)
      }
    },
    currentUserSorts () {
      return userSortsFromHeader(this.sortBy, this.fields)
    },
    readSearchFromUrl () {
      try {
        const params = new URLSearchParams(window.location.search)
        this.searchText = params.get(this.searchQueryKey) || ''
      } catch (err) {
        this.searchText = ''
      }
    },
    writeSearchToUrl () {
      try {
        const url = new URL(window.location.href)
        if (this.searchTerm) url.searchParams.set(this.searchQueryKey, this.searchTerm)
        else url.searchParams.delete(this.searchQueryKey)
        window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`)
      } catch (err) {
        console.warn('Failed to write search to URL:', err)
      }
    },
    onSearchInput (value) {
      this.searchText = value
      clearTimeout(this._searchTimer)
      this._searchTimer = setTimeout(() => {
        this.writeSearchToUrl()
        this.fetchTableData()
      }, 300)
    },
    reloadForPresetFilters () {
      return this.fetchTableData({ alwaysApply: true })
    },
    async refreshData () {
      await this.fetchTableData({ alwaysApply: true })
    },
    async onFilterChanged (filterEvent) {
      this.writeFiltersToUrl(filterEvent)
      this.activeFilterEvent = filterEvent
      await this.fetchTableData()
    },
    async onSortByUpdate (sortBy) {
      this.sortBy = sortBy || []
      await this.fetchTableData()
    },
    tableRequestParams () {
      const params = new URLSearchParams()
      const filterEvent = this.activeFilterEvent
      const hasFilters = filterEvent && filterEvent.filters && filterEvent.filters.length > 0

      if (hasFilters) {
        params.set('userFilters', JSON.stringify(filterEvent.filters))
        params.set('userFilterMatchMode', filterEvent.matchMode || 'all')
      }

      const userSorts = this.currentUserSorts()
      if (userSorts.length) {
        params.set('userSorts', JSON.stringify(userSorts))
      }

      if (this.searchEnabled && this.searchTerm) {
        params.set('search', this.searchTerm)
      }

      if (this.presetFilterQuery()) {
        params.set('presetFilters', this.presetFilterQuery())
      }

      const accessKey = this.routeAccessKey
      if (accessKey) params.set('accessKey', accessKey)

      return params
    },
    componentRequestUrl (suffix, extra) {
      const pageUT = this.pageUniqueTitle || this.routePageUniqueTitle || window.__superturbo_getPageUniqueTitle?.()
      const compUT = this.componentUniqueTitle || this.uniqueTitle
      const account = this.routeAccount
      const application = this.$route?.params?.application || ''
      const params = this.tableRequestParams()

      if (extra) {
        for (const [key, value] of Object.entries(extra)) {
          if (value != null && value !== '') params.set(key, String(value))
        }
      }

      const qs = params.toString()
      const path = `${VUE_APP_PROXY_SERVER_URL}/${account}/${application}/pages/${pageUT}/components/${compUT}${suffix || ''}`

      return qs ? `${path}?${qs}` : path
    },
    noteFullPage (rows) {

      if (this.filteredItems === null && this.chosenPageSize) {

        this.hasNextPage = (rows || []).length >= this.chosenPageSize
      }
    },
    columnHidden (field) {

      if (!this.layout.hideEmptyColumns || !field || field.type === 'action' || !field.columnUniqueTitle) return false

      return this.displayItems.every((item) => isEmptyCell(item && item._values && item._values[field.columnUniqueTitle]))
    },
    listDisplayFor (columnUniqueTitle) {

      const field = (this.fields || []).find((item) => item.columnUniqueTitle === columnUniqueTitle)

      return field && field.multiselectDisplay
    },
    shownCell (data) {

      const field = data.field || {}
      const rawRow = data.item && data.item._raw
      const raw = rawRow && rawRow[field.key] != null ? rawRow[field.key] : data.value
      const shown = shownField(fieldDisplayValue(raw, data.value), {
        multiselectDisplay: field.multiselectDisplay,
        valueFormatting: field.valueFormatting
      })

      if (shown) return shown.html

      return data.value == null ? '' : String(data.value)
    },
    blankCell (data) {

      if (!data.field || !data.field.emptyText) return false

      return isEmptyCell(this.getRawValue(data.item, data.field.key))
    },
    actionVisible (item, field) {

      return tableActionAllowed(field.submissionRules, item && item._values)
    },
    async loadSummary () {

      if (window.__superturbo_preview_mode) return
      if (!(this.fields || []).some((field) => field.summary)) return

      try {

        const response = await axios.get(this.componentRequestUrl('/summary'))

        this.columnSummary = (response.data && response.data.columns) || {}
      } catch (err) {

        this.columnSummary = {}
      }
    },
    async changePageSize (value) {

      this.chosenPageSize = clampPageSize(Number(value), this.tableLayout)
      this.pageOffset = 0
      await this.loadPage()
    },
    async changePage (direction) {

      const size = this.chosenPageSize || 25
      const next = this.pageOffset + direction * size

      if (next < 0) return

      this.pageOffset = next
      await this.loadPage()
    },
    async loadPage () {

      if (!this.chosenPageSize) return

      const url = this.componentRequestUrl('', { limit: this.chosenPageSize, offset: this.pageOffset })
      const response = await axios.get(url)
      const rows = Array.isArray(response.data) ? response.data : []

      this.hasNextPage = rows.length === this.chosenPageSize
      this.filteredItems = rows.map((row) => this.formatLoadedRow(row))
      this.loadSummary()
    },
    formatLoadedRow (row) {

      const item = { _rowId: row.id, _values: row, _raw: {}, _formatting: row._formatting, _links: row._links, links: row.links }

      for (const field of this.fields || []) {

        if (!field.columnUniqueTitle) continue

        const value = row[field.columnUniqueTitle]

        item._raw[field.key] = value
        item[field.key] = value
      }

      return item
    },
    startResize (field, event) {

      const startX = event.clientX
      const start = this.columnWidths[field.key] || field.width || 160

      const move = (next) => {

        const width = Math.max(48, Math.min(2000, start + next.clientX - startX))

        this.columnWidths = { ...this.columnWidths, [field.key]: width }
      }

      const stop = () => {

        window.removeEventListener('mousemove', move)
        window.removeEventListener('mouseup', stop)
      }

      window.addEventListener('mousemove', move)
      window.addEventListener('mouseup', stop)
    },
    async runAction (item, field) {

      const key = `${item._rowId}_${field.columnUniqueTitle}`

      this.actionState[key] = 'running'
      this.actionErrors[key] = ''

      try {

        const pageUT = this.pageUniqueTitle || this.routePageUniqueTitle || window.__superturbo_getPageUniqueTitle?.()
        const compUT = this.componentUniqueTitle || this.uniqueTitle
        const account = this.routeAccount
        const application = this.$route?.params?.application || ''

        await axios.post(
          `${VUE_APP_PROXY_SERVER_URL}/${account}/${application}/pages/${pageUT}/components/${compUT}/actions/${field.columnUniqueTitle}`,
          { recordId: item._rowId }
        )
        this.actionState[key] = 'done'

        if (this.chosenPageSize) await this.loadPage()
      } catch (err) {

        const data = err && err.response && err.response.data

        this.actionErrors[key] = typeof data === 'string' && data.trim() ? data : 'The action could not be completed.'
        this.actionState[key] = 'error'
      }
    },
    exportDownloadName (format) {

      const base = String(this.title || this.uniqueTitle || 'export').replace(/[\\/:*?"<>|]+/g, ' ').trim() || 'export'
      const extension = format === 'json' ? 'json' : (format === 'text' ? 'txt' : 'csv')

      return `${base}.${extension}`
    },
    async exportTable (format) {
      if (window.__superturbo_preview_mode || this.csvExporting) return

      this.csvExporting = true

      try {
        const response = await axios.get(this.componentRequestUrl('/export', { format }), {
          responseType: 'blob'
        })
        const blob = response.data instanceof Blob
          ? response.data
          : new Blob([response.data])
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = this.exportDownloadName(format)
        document.body.appendChild(link)
        link.click()
        link.remove()
        URL.revokeObjectURL(url)
      } catch (err) {
        console.warn('Failed to export table:', err)
      } finally {
        this.csvExporting = false
      }
    },
    async fetchTableData ({ alwaysApply = false } = {}) {
      const userSorts = this.currentUserSorts()
      const filterEvent = this.activeFilterEvent
      const hasFilters = filterEvent && filterEvent.filters && filterEvent.filters.length > 0
      const hasSearch = this.searchEnabled && !!this.searchTerm
      const hasPreset = this.activePresetFilterIds.length > 0

      if (!alwaysApply && !hasFilters && !userSorts.length && !hasSearch && !hasPreset) {
        this.filteredItems = null
        return
      }

      const url = this.componentRequestUrl('', { limit: 25 })

      const requestId = (this._tableRequestId = (this._tableRequestId || 0) + 1)

      try {
        const response = await axios.get(url)
        if (requestId !== this._tableRequestId) return
        this.filteredItems = this.transformResponseData(response.data)
      } catch (err) {
        if (requestId !== this._tableRequestId) return
        console.warn('Failed to fetch table data:', err)
      }
    },
    connectRealtimeWebSocket () {
      try {
        this.ws = new WebSocket(liveAppWebSocketUrl(VUE_APP_APP_SERVER_WS_URL, this.routeAccount, this.$route.params.application))
      } catch (err) {
        console.warn('Realtime WebSocket connection failed:', err)
        return
      }

      this.ws.onopen = () => {
        this.ws.send(JSON.stringify({
          type: 'realtime_subscribe',
          pageUniqueTitle: this.pageUniqueTitle,
          componentUniqueTitle: this.componentUniqueTitle,
          spreadSheetUniqueTitle: this.spreadSheetUniqueTitle,
          ...realtimeSubscribeFields({
            ...(this.$route && this.$route.params),
            account: this.routeAccount
          })
        }))
      }

      this.ws.onmessage = (event) => {
        let msg
        try {
          msg = JSON.parse(event.data)
        } catch { return }

        if (msg.type === 'realtime_update' &&
            msg.pageUniqueTitle === this.pageUniqueTitle &&
            msg.componentUniqueTitle === this.componentUniqueTitle) {
          if (this.currentUserSorts().length || (this.searchEnabled && this.searchTerm) || this.activePresetFilterIds.length) {
            this.fetchTableData({ alwaysApply: true })
          } else {
            this.filteredItems = this.transformResponseData(msg.data)
          }
        }
      }

      this.ws.onclose = () => {
        setTimeout(() => {
          if (this.realtime && !this._isBeingDestroyed) {
            this.connectRealtimeWebSocket()
          }
        }, 3000)
      }

      this.ws.onerror = () => {}
    },
    disconnectRealtimeWebSocket () {
      if (this.ws) {
        if (this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({
            type: 'realtime_unsubscribe',
            pageUniqueTitle: this.pageUniqueTitle,
            componentUniqueTitle: this.componentUniqueTitle
          }))
        }
        this.ws.onclose = null
        this.ws.close()
        this.ws = null
      }
    },
    buildFormatStyleString (fmt) {
      return formattingToStyleString(fmt)
    },
    transformResponseData (data) {
      const fcm = fieldIndexMap(this.fieldCounterMap, this.fields)
      if (!fcm) return data

      return _.map(data, (row) => {
        const newRow = {}
        const rawRow = {}
        const formatting = row._formatting || null

        // Preserve row ID for editable saves
        if (row.id !== undefined) newRow._rowId = row.id
        if (row._id !== undefined) newRow._rowId = row._id

        for (const key of Object.keys(row)) {
          if (key.endsWith('_filename') || key.endsWith('_type') || key === '_formatting') continue
          const column = row[key]
          if (typeof fcm[key] !== 'number') continue

          // Store raw value for editable fields
          rawRow[fcm[key]] = column

          const imageHtml = imageCellHtml(row, key)
          if (imageHtml != null) {
            newRow[fcm[key]] = imageHtml
          } else {
            const listHtml = formatMultiselectHtml(column, this.listDisplayFor(key))
            let cellHtml

            if (listHtml != null) {
              cellHtml = listHtml
            } else {
              const text = column == null ? '' : (typeof column === 'string' ? formatAppInstant(column) : String(column))
              cellHtml = escapeHtml(text)
            }

            if (formatting && formatting[key]) {
              const fmt = formatting[key]
              if (fmt.hidden) {
                cellHtml = ''
              } else {
                const styleStr = this.buildFormatStyleString(fmt)
                if (styleStr) {
                  cellHtml = `<span style="${styleStr}">${cellHtml}</span>`
                }
              }
            }

            newRow[fcm[key]] = cellHtml
          }
        }

        // Preserve link data from server
        if (row._links) newRow._links = row._links
        if (row.links) newRow.links = row.links

        newRow._raw = rawRow
        return newRow
      })
    }
  }
}
</script>

<style>
.table-wrap-headers :deep(th) {
  white-space: normal;
}

.table-head-label {
  display: inline;
}

.table-col-resize {
  display: inline-block;
  width: 6px;
  margin-left: 4px;
  cursor: col-resize;
  user-select: none;
}

.table-col-resize::after {
  content: '';
  display: inline-block;
  width: 2px;
  height: 0.9em;
  background: #adb5bd;
  vertical-align: text-bottom;
}

.table-summary {
  margin-top: 0.5rem;
  font-weight: 600;
}

.table-page-size {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.table-page-size-select {
  margin-left: 0.35rem;
  border: 1px solid #ced4da;
  background: #fff;
  color: #212529;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 0.875rem;
  line-height: 1.5;
}

.visually-hidden {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}
.table-responsive img.rich-text-image,
.table-responsive .rich-text-image {
  max-width: 200px;
  max-height: 150px;
  object-fit: cover;
  cursor: pointer;
  vertical-align: middle;
  margin: 2px 4px;
}

.table-responsive pre {
  padding: 8px;
  margin: 4px 0;
  font-size: 13px;
  max-width: 400px;
  overflow-x: auto;
}

.table-responsive code {
  padding: 1px 4px;
  font-size: 13px;
}

/* ── Editable Cells ── */
.editable-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.editable-cell-input {
  width: 100%;
  padding: 4px 8px;
  font-size: 0.9em;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background: #fff;
  color: #212529;
  transition: border-color 0.15s, box-shadow 0.15s, background-color 0.3s;
}

.editable-cell-input:focus {
  border-color: #86b7fe;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.15);
}

.editable-cell-input.editable-cell-saving {
  background-color: #fff8e1;
  border-color: #ffc107;
}

.editable-cell-input.editable-cell-saved {
  background-color: #e8f5e9;
  border-color: #4caf50;
}

.editable-cell-error {
  display: block;
  color: #b42318;
  font-size: 0.75rem;
  margin-top: 2px;
}

.editable-cell-desc {
  color: #6c757d;
  font-size: 0.78em;
  line-height: 1.3;
}

/* ── Table Links ── */
.table-link {
  text-decoration: none;
  font-weight: 500;
  transition: color 0.15s ease;
}

button.table-code-link {
  background: none;
  border: 0;
  padding: 0;
  color: inherit;
  cursor: pointer;
  font: inherit;
}

.table-link:hover {
  text-decoration: underline;
}

.table-link.view-link-button,
.table-link.view-link-button:hover {
  color: #fff;
  text-decoration: none;
}

.table-link-disabled {
  color: #999;
  font-style: italic;
}

.table-export-row {
  margin-bottom: 8px;
}

.table-export-btn {
  border: 1px solid #ced4da;
  background: #fff;
  color: #212529;
  border-radius: 4px;
  padding: 4px 12px;
  font-size: 0.875rem;
  line-height: 1.5;
  cursor: pointer;
}

.table-export-btn:hover:not(:disabled) {
  background: #f8f9fa;
}

.table-export-btn:disabled {
  opacity: 0.65;
  cursor: default;
}
</style>
